import { memo, useState, useCallback, useMemo, useRef } from "react"
import { createPortal } from "react-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { showErrorToast } from "@/components/NotificationToast"
import { GitMerge, Lock, Plus, Trash2, Undo2 } from "lucide-react"
import type { CurrencyRepresentation, ExtractedField, FieldOption } from "../../types"
import {
  resolveSelectValue,
  type EditableValueRenderer,
  type LineItemRow
} from "../../utils/invoiceFieldHelpers"
import { updateRepresentationLineItem } from "@/services/invoiceService"
import { useErpDealNames, useErpAccAssignments, useErpAnalyticPartsCache, type AnalyticPartsCache } from "../../hooks/useErpOptions"
import { EditableSelectField } from "./EditableSelectField"
import { LineItemErpRow } from "./LineItemErpRow"

interface LineItemErpMetadata {
  deal_name?: number
  acc_assignment?: number
  analytic_parts?: Array<{
    acc: string
    level1?: string
    level1_name?: string
    level2?: string
    level2_name?: string
    level3?: string
    level3_name?: string
  }>
  without_account_assignment?: boolean
}

interface LineItemsCardProps {
  lineItemRows: LineItemRow[]
  selectedLineItemIds: string[]
  areAllLineItemsSelected: boolean
  normalizedUnitOfMeasureSelectOptions: FieldOption[]
  editableFieldAttr: Record<string, string>
  focusTargetAttr: Record<string, string>
  hasSelection: boolean
  hasMergeableSelection: boolean
  isMergePending: boolean
  clientId?: string
  invoiceId?: string
  lineItemsErpMetadata?: Record<string, LineItemErpMetadata>
  onClearSelection: () => void
  onAddLineItem: () => void
  onMergeSelected: () => void
  onRowSelectionChange: (rowId: string, checked: boolean) => void
  onSelectAllLineItems: (checked: boolean) => void
  onDeleteRow: (rowId: string) => void | Promise<void>
  onUnitChange: (field: ExtractedField, value: string) => void | Promise<void>
  onFieldUpdate?: (fieldId: string, newValue: string) => void
  onRepresentationUpdate?: (updatedRepr: CurrencyRepresentation) => void
  onLineItemErpMetadataChange?: (lineItemGroup: string, metadata: LineItemErpMetadata) => void
  onRestoreLineItems?: (mergedGroupId: string) => Promise<void> | void
  mergedGroupIds?: Set<string>
  renderEditableValue: EditableValueRenderer
  isIncome?: boolean
  readOnly?: boolean
}

const renderEditableLineItemValue = (
  field: ExtractedField | undefined,
  renderEditableValue: EditableValueRenderer,
  options?: { truncate?: boolean; readOnly?: boolean }
): JSX.Element =>
  renderEditableValue(field, {
    placeholder: "-",
    buttonClassName: options?.truncate
      ? "px-1 py-0.5 rounded truncate !whitespace-nowrap block"
      : "px-1 py-0.5 rounded",
    containerClassName: "space-x-1",
    inputClassName: "px-1 py-0.5 text-sm",
    readOnly: options?.readOnly
  })

/** Compute per-line tax: (net - discount) * vatRate / 100 where net = qty * unitPrice */
const computeLineTax = (
  quantity: string | null,
  unitPrice: string | null,
  vatRate: string | null,
  discountPercent: string | null = null,
  discountAmount: string | null = null,
): string | null => {
  const qty = parseFloat(quantity ?? "")
  const price = parseFloat(unitPrice ?? "")
  const vat = parseFloat(vatRate ?? "")
  if (isNaN(qty) || isNaN(price) || isNaN(vat)) return null
  const preDiscountNet = qty * price
  let discount = 0
  const parsedAmt = parseFloat(discountAmount ?? "")
  const parsedPct = parseFloat(discountPercent ?? "")
  if (!isNaN(parsedAmt) && parsedAmt > 0) {
    discount = parsedAmt
  } else if (!isNaN(parsedPct) && parsedPct > 0) {
    discount = preDiscountNet * parsedPct / 100
  }
  const taxableBase = preDiscountNet - discount
  const tax = taxableBase * vat / 100
  return tax.toFixed(4)
}

/** Inline editable cell for representation monetary fields */
const RepresentationCell = memo(({
  value,
  field,
  lineItemGroup,
  invoiceId,
  onRepresentationUpdate,
  placeholder = "-",
  className,
  readOnly,
}: {
  value: string | null
  field: string
  lineItemGroup: string
  invoiceId?: string
  onRepresentationUpdate?: (updatedRepr: CurrencyRepresentation) => void
  placeholder?: string
  className?: string
  readOnly?: boolean
}) => {
  const [editing, setEditing] = useState(false)
  const [localValue, setLocalValue] = useState(value ?? "")
  const [saving, setSaving] = useState(false)

  const handleSave = useCallback(async () => {
    setEditing(false)
    const trimmed = localValue.trim()
    if (trimmed === (value ?? "")) return
    if (!invoiceId) return

    setSaving(true)
    try {
      const result = await updateRepresentationLineItem(invoiceId, lineItemGroup, field, trimmed)
      if (result?.representation) {
        onRepresentationUpdate?.(result.representation)
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : "Failed to update")
      setLocalValue(value ?? "")
    } finally {
      setSaving(false)
    }
  }, [localValue, value, invoiceId, lineItemGroup, field, onRepresentationUpdate])

  if (editing) {
    return (
      <input
        type="text"
        className={cn("w-full rounded border border-blue-300 bg-white px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400", className)}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={() => void handleSave()}
        onKeyDown={(e) => {
          if (e.key === "Enter") void handleSave()
          if (e.key === "Escape") {
            setLocalValue(value ?? "")
            setEditing(false)
          }
        }}
        autoFocus
      />
    )
  }

  if (readOnly) {
    return (
      <span className={cn("w-full rounded px-1 py-0.5 text-left text-sm text-neutral-500", className)}>
        {value || <span className="text-gray-400 italic">{placeholder}</span>}
      </span>
    )
  }

  const activate = () => {
    setLocalValue(value ?? "")
    setEditing(true)
  }

  return (
    <button
      type="button"
      className={cn(
        "w-full cursor-text rounded px-1 py-0.5 text-left text-sm hover:bg-neutral-100 transition-colors",
        saving && "opacity-50",
        className
      )}
      onClick={activate}
      onFocus={activate}
      disabled={saving || !invoiceId}
    >
      {value || <span className="text-gray-400 italic">{placeholder}</span>}
    </button>
  )
})
RepresentationCell.displayName = "RepresentationCell"

/** Memoized individual line item row */
interface LineItemRowCardProps {
  row: LineItemRow
  isSelected: boolean
  normalizedUnitOfMeasureSelectOptions: FieldOption[]
  editableFieldAttr: Record<string, string>
  focusTargetAttr: Record<string, string>
  invoiceId?: string
  clientId?: string
  erpMetadata?: LineItemErpMetadata
  isMerged: boolean
  readOnly?: boolean
  onRowSelectionChange: (rowId: string, checked: boolean) => void
  onDeleteRow: (rowId: string) => void | Promise<void>
  onUnitChange: (field: ExtractedField, value: string) => void | Promise<void>
  onFieldUpdate?: (fieldId: string, newValue: string) => void
  onRepresentationUpdate?: (updatedRepr: CurrencyRepresentation) => void
  onLineItemErpMetadataChange?: (lineItemGroup: string, metadata: LineItemErpMetadata) => void
  onRestoreLineItems?: (mergedGroupId: string) => Promise<void> | void
  renderEditableValueRef: React.MutableRefObject<EditableValueRenderer>
  erpDealNames: import("@/lib/api").DealName[]
  erpDealNamesLoading: boolean
  erpAccAssignments: import("@/lib/api").AccAssignment[]
  erpAccAssignmentsLoading: boolean
  analyticPartsCache: AnalyticPartsCache
}

const LineItemRowCard = memo(({
  row,
  isSelected,
  normalizedUnitOfMeasureSelectOptions,
  editableFieldAttr,
  focusTargetAttr,
  invoiceId,
  clientId,
  erpMetadata,
  isMerged,
  readOnly,
  onRowSelectionChange,
  onDeleteRow,
  onUnitChange,
  onFieldUpdate,
  onRepresentationUpdate,
  onLineItemErpMetadataChange,
  onRestoreLineItems,
  renderEditableValueRef,
  erpDealNames,
  erpDealNamesLoading,
  erpAccAssignments,
  erpAccAssignmentsLoading,
  analyticPartsCache,
}: LineItemRowCardProps) => {
  const unitOfMeasure = row.unitOfMeasure
  const hasUnitOptions = normalizedUnitOfMeasureSelectOptions.length > 0
  const resolvedUnitValue =
    unitOfMeasure && hasUnitOptions
      ? resolveSelectValue(unitOfMeasure.field_value, normalizedUnitOfMeasureSelectOptions) ??
      unitOfMeasure.field_value ??
      ""
      : unitOfMeasure?.field_value ?? ""
  const unitSelectValue = resolvedUnitValue === "" ? "__clear" : resolvedUnitValue
  const trimmedFieldValue = unitOfMeasure?.field_value?.trim()
  const needsCustomOption =
    !!trimmedFieldValue &&
    !normalizedUnitOfMeasureSelectOptions.some((option) => option.value === trimmedFieldValue)

  // Use the ref to always call the latest renderEditableValue without causing re-renders
  const stableRenderEditableValue: EditableValueRenderer = useCallback(
    (field, options) => renderEditableValueRef.current(field, options),
    [renderEditableValueRef]
  )

  return (
    <div
      className={cn(
        "rounded-lg border border-neutral-200 p-4 transition-colors",
        isSelected ? "border-blue-300 bg-blue-50/60" : "hover:border-neutral-300"
      )}
    >
      <div className="flex items-start gap-3">
        {!readOnly && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => onRowSelectionChange(row.id, checked)}
            aria-label="Select line item"
            className="mt-1 h-4 w-4 border-neutral-300 bg-white shadow-sm transition-colors duration-150 focus-visible:ring-blue-500"
          />
        )}
        <div className="flex-1 space-y-2">
          {/* Row 1: Description, Qty, Unit */}
          <div className="grid grid-cols-12 gap-3 text-sm">
            <div className="flex flex-col col-span-8 space-y-1 min-w-0">
              <span className="text-xs text-gray-500">Description</span>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="font-medium min-w-0 overflow-hidden">
                      {renderEditableLineItemValue(row.description, stableRenderEditableValue, { truncate: true, readOnly })}
                    </div>
                  </TooltipTrigger>
                  {row.description?.field_value && (
                    <TooltipContent side="top" className="max-w-sm">
                      <p>{row.description.field_value}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="flex flex-col col-span-1 space-y-1">
              <span className="text-xs text-gray-500">Qty</span>
              <div className="font-medium">
                <RepresentationCell
                  value={row.quantity}
                  field="quantity"
                  lineItemGroup={row.id}
                  invoiceId={invoiceId}
                  onRepresentationUpdate={onRepresentationUpdate}
                  readOnly={readOnly}
                />
              </div>
            </div>

            <div className="flex flex-col col-span-3 space-y-1">
              <span className="text-xs text-gray-500">Unit</span>
              <div className="font-medium">
                {unitOfMeasure ? (
                  <EditableSelectField
                    value={unitSelectValue}
                    placeholder="Select unit"
                    options={normalizedUnitOfMeasureSelectOptions}
                    onChange={(value) => onUnitChange(unitOfMeasure, value)}
                    editableFieldAttr={editableFieldAttr}
                    focusTargetAttr={focusTargetAttr}
                    disabled={!onFieldUpdate || readOnly}
                    extraOptions={
                      needsCustomOption && trimmedFieldValue
                        ? [{ label: trimmedFieldValue, value: trimmedFieldValue }]
                        : undefined
                    }
                  />
                ) : (
                  <span className="text-gray-400 italic">-</span>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Unit Price, Disc%, Disc Amt, VAT%, Tax, Total */}
          <div className="grid grid-cols-12 gap-3 text-sm">
            <div className="flex flex-col col-span-2 space-y-1">
              <span className="text-xs text-gray-400">Unit Price</span>
              <div className="text-gray-700">
                <RepresentationCell
                  value={row.unitPrice}
                  field="unit_price"
                  lineItemGroup={row.id}
                  invoiceId={invoiceId}
                  onRepresentationUpdate={onRepresentationUpdate}
                  readOnly={readOnly}
                />
              </div>
            </div>

            <div className="flex flex-col col-span-1 space-y-1">
              <span className="text-xs text-gray-400">Disc%</span>
              <div className="text-gray-700">
                <RepresentationCell
                  value={row.discountPercent}
                  field="discount_percent"
                  lineItemGroup={row.id}
                  invoiceId={invoiceId}
                  onRepresentationUpdate={onRepresentationUpdate}
                  readOnly={readOnly}
                />
              </div>
            </div>

            <div className="flex flex-col col-span-2 space-y-1">
              <span className="text-xs text-gray-400">Disc Amt</span>
              <div className="text-gray-700">
                <RepresentationCell
                  value={row.discountAmount}
                  field="discount_amount"
                  lineItemGroup={row.id}
                  invoiceId={invoiceId}
                  onRepresentationUpdate={onRepresentationUpdate}
                  readOnly={readOnly}
                />
              </div>
            </div>

            <div className="flex flex-col col-span-1 space-y-1">
              <span className="text-xs text-gray-400">VAT%</span>
              <div className="text-gray-700">
                <RepresentationCell
                  value={row.vatRate}
                  field="vat_rate"
                  lineItemGroup={row.id}
                  invoiceId={invoiceId}
                  onRepresentationUpdate={onRepresentationUpdate}
                  readOnly={readOnly}
                />
              </div>
            </div>

            <div className="flex flex-col col-span-2 space-y-1">
              <span className="text-xs text-gray-400">Tax</span>
              <div className="text-gray-700">
                <RepresentationCell
                  value={row.lineTax ?? computeLineTax(row.quantity, row.unitPrice, row.vatRate, row.discountPercent, row.discountAmount)}
                  field="line_tax"
                  lineItemGroup={row.id}
                  invoiceId={invoiceId}
                  onRepresentationUpdate={onRepresentationUpdate}
                  readOnly={readOnly}
                />
              </div>
            </div>

            <div className="flex flex-col col-span-2 space-y-1">
              <span className="text-xs text-gray-400">Total</span>
              <div className="text-gray-700 text-sm px-1 py-0.5">
                {row.lineTotal || <span className="text-gray-400 italic">-</span>}
              </div>
            </div>

            <div className="col-span-2" />
          </div>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-0.5">
            {isMerged && onRestoreLineItems && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-neutral-500 hover:text-amber-600"
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      void onRestoreLineItems(row.id)
                    }}
                    aria-label="Undo merge"
                  >
                    <Undo2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">Undo merge — restore original items</TooltipContent>
              </Tooltip>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="text-neutral-500 hover:text-red-600"
              type="button"
              onClick={(event) => {
                event.stopPropagation()
                void onDeleteRow(row.id)
              }}
              aria-label="Delete line item"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* ERP Metadata Row */}
      <LineItemErpRow
        lineItemGroup={row.id}
        clientId={clientId}
        erpMetadata={erpMetadata}
        onMetadataChange={onLineItemErpMetadataChange ?? (() => { })}
        disabled={!onLineItemErpMetadataChange || readOnly}
        dealNames={erpDealNames}
        isLoadingDealNames={erpDealNamesLoading}
        accAssignments={erpAccAssignments}
        isLoadingAccAssignments={erpAccAssignmentsLoading}
        analyticPartsCache={analyticPartsCache}
        withoutAccountAssignment={erpMetadata?.without_account_assignment}
      />
    </div>
  )
})
LineItemRowCard.displayName = "LineItemRowCard"

export const LineItemsCard = ({
  lineItemRows,
  selectedLineItemIds,
  areAllLineItemsSelected,
  normalizedUnitOfMeasureSelectOptions,
  editableFieldAttr,
  focusTargetAttr,
  hasSelection,
  hasMergeableSelection,
  isMergePending,
  clientId,
  invoiceId,
  lineItemsErpMetadata,
  onClearSelection,
  onAddLineItem,
  onMergeSelected,
  onRowSelectionChange,
  onSelectAllLineItems,
  onDeleteRow,
  onUnitChange,
  onFieldUpdate,
  onRepresentationUpdate,
  onLineItemErpMetadataChange,
  onRestoreLineItems,
  mergedGroupIds,
  renderEditableValue,
  isIncome,
  readOnly,
}: LineItemsCardProps): JSX.Element => {
  // Store renderEditableValue in a ref so memoized rows can access the latest
  // version without needing it as a prop (which changes on every fieldEditor state update)
  const renderEditableValueRef = useRef(renderEditableValue)
  renderEditableValueRef.current = renderEditableValue

  // Fetch ERP data once, shared across all line item rows
  const { dealNames: erpDealNames, isLoading: erpDealNamesLoading } = useErpDealNames(clientId, isIncome)
  const { accAssignments: erpAccAssignments, isLoading: erpAccAssignmentsLoading } = useErpAccAssignments(clientId, isIncome)
  const analyticPartsCache = useErpAnalyticPartsCache(clientId, isIncome)

  // Tri-state "Include account assignment" toggle all
  const withoutAccCounts = useMemo(() => {
    const ids = lineItemRows.map((r) => r.id)
    let onCount = 0
    let offCount = 0
    for (const id of ids) {
      if (lineItemsErpMetadata?.[id]?.without_account_assignment) {
        offCount++
      } else {
        onCount++
      }
    }
    return { onCount, offCount, total: ids.length }
  }, [lineItemRows, lineItemsErpMetadata])

  const allIncludeAcc = withoutAccCounts.offCount === 0
  const noneIncludeAcc = withoutAccCounts.onCount === 0
  const includeAccChecked = allIncludeAcc ? true : noneIncludeAcc ? false : "indeterminate"

  const handleToggleAllAccAssignment = useCallback(() => {
    if (!onLineItemErpMetadataChange) return
    // If all are currently included (or mixed), toggle all to excluded
    // If none are included, toggle all to included
    const setWithout = !noneIncludeAcc

    for (const row of lineItemRows) {
      const existing = lineItemsErpMetadata?.[row.id] ?? {}
      onLineItemErpMetadataChange(row.id, {
        ...existing,
        without_account_assignment: setWithout,
      })
    }
  }, [lineItemRows, lineItemsErpMetadata, onLineItemErpMetadataChange, noneIncludeAcc])

  if (lineItemRows.length === 0) {
    return (
      <Card className="w-full border border-neutral-200 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="font-text-sm-medium text-neutral-800">Line Items</h3>
            <span className="text-xs text-gray-500">(0 items)</span>
          </div>
          <div className="rounded-lg border border-dashed border-neutral-200 py-6 text-center text-sm text-gray-400">
            No line items available
          </div>
          {!readOnly && (
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={onAddLineItem} type="button">
                <Plus className="h-4 w-4" />
                Add line item
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <>
    <Card className="w-full border border-neutral-200 shadow-sm">
      <CardContent className={cn("relative p-6 space-y-4")}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <h3 className="font-text-sm-medium text-neutral-800">Line Items</h3>
            <span className="text-xs text-gray-500">({lineItemRows.length} items)</span>
            {readOnly && (
              <span className="inline-flex items-center gap-1 text-xs text-neutral-400">
                <Lock className="h-3 w-3" />
                Locked
              </span>
            )}
          </div>
          {!readOnly && (
            <label className="flex items-center gap-2 text-xs font-medium text-neutral-600">
              <Checkbox
                checked={areAllLineItemsSelected}
                onCheckedChange={onSelectAllLineItems}
                aria-label="Select all line items"
                className="border-neutral-300 bg-white shadow-sm transition-colors"
              />
              Select all
            </label>
          )}
        </div>

        {!readOnly && lineItemRows.length > 0 && (
          <label className="flex items-center gap-2 text-xs font-medium text-neutral-600">
            <Checkbox
              checked={includeAccChecked}
              onCheckedChange={handleToggleAllAccAssignment}
              aria-label="Include account assignment for all"
              className="border-blue-400 bg-white shadow-sm transition-colors data-[state=checked]:bg-[#0000FF] data-[state=checked]:border-[#0000FF] data-[state=indeterminate]:bg-[#0000FF] data-[state=indeterminate]:border-[#0000FF]"
            />
            Include all account assignments
          </label>
        )}

        <div className="space-y-4">
          {lineItemRows.map((row) => (
            <LineItemRowCard
              key={row.id}
              row={row}
              isSelected={selectedLineItemIds.includes(row.id)}
              normalizedUnitOfMeasureSelectOptions={normalizedUnitOfMeasureSelectOptions}
              editableFieldAttr={editableFieldAttr}
              focusTargetAttr={focusTargetAttr}
              invoiceId={invoiceId}
              clientId={clientId}
              erpMetadata={lineItemsErpMetadata?.[row.id]}
              isMerged={mergedGroupIds?.has(row.id) ?? false}
              readOnly={readOnly}
              onRowSelectionChange={onRowSelectionChange}
              onDeleteRow={onDeleteRow}
              onUnitChange={onUnitChange}
              onFieldUpdate={onFieldUpdate}
              onRepresentationUpdate={onRepresentationUpdate}
              onLineItemErpMetadataChange={onLineItemErpMetadataChange}
              onRestoreLineItems={onRestoreLineItems}
              renderEditableValueRef={renderEditableValueRef}
              erpDealNames={erpDealNames}
              erpDealNamesLoading={erpDealNamesLoading}
              erpAccAssignments={erpAccAssignments}
              erpAccAssignmentsLoading={erpAccAssignmentsLoading}
              analyticPartsCache={analyticPartsCache}
            />
          ))}
        </div>

        {!readOnly && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={onAddLineItem} type="button">
              <Plus className="h-4 w-4" />
              Add line item
            </Button>
          </div>
        )}

      </CardContent>
    </Card>

    {!readOnly && hasSelection && createPortal(
      <div
        className="fixed bottom-20 right-6 z-50 rounded-lg border border-neutral-200 bg-white px-5 py-3 shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-200"
      >
        <div className="flex items-center gap-4">
          <span className="text-sm text-neutral-600">{selectedLineItemIds.length} selected</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" type="button" onClick={onClearSelection}>
              Clear
            </Button>
            <Button
              size="sm"
              type="button"
              variant="blue"
              onClick={onMergeSelected}
              disabled={!hasMergeableSelection || isMergePending}
            >
              {isMergePending ? (
                "Merging..."
              ) : (
                <>
                  <GitMerge className="h-4 w-4" />
                  Merge
                </>
              )}
            </Button>
          </div>
        </div>
      </div>,
      document.body
    )}
    </>
  )
}
